import React, { useState, useRef, useEffect } from "react";
import {
  FiX,
  FiSave,
  FiActivity,
  FiChevronDown,
  FiChevronUp,
  FiMaximize2,
  FiMinimize2,
  FiZap,
  FiCheck,
  FiEdit2,
  FiTrash2,
  FiMic,
  FiMicOff,
  FiPlay,
  FiPause,
  FiTrash,
} from "react-icons/fi";
import { Avaliation } from "../../types";
import ImageInput from "../ui/ImageInput";

/**
 * Componentes auxiliares para o AvaliacaoForm
 */

interface ModalHeaderProps {
  title: string;
  subtitle: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
}

export function ModalHeader({
  title,
  subtitle,
  isExpanded,
  onToggleExpand,
  onClose,
}: ModalHeaderProps) {
  return (
    <div className="relative border-b border-stroke bg-gradient-to-r from-purple-50 via-purple-50 to-purple-50 px-3 py-3 sm:px-6 sm:py-5 md:px-8 md:py-6 dark:border-strokedark dark:from-purple-950/30 dark:via-purple-950/30 dark:to-purple-950/30">
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      <div className="absolute right-2 top-2 sm:right-4 sm:top-4 md:right-6 md:top-6 z-10 flex items-center gap-1 sm:gap-1.5 md:gap-2">
        <button
          type="button"
          onClick={onToggleExpand}
          className="hidden sm:flex rounded-lg md:rounded-xl p-1.5 sm:p-2 md:p-2.5 text-gray-500 outline-none transition-all active:bg-white active:text-gray-700 md:hover:bg-white md:hover:text-gray-700 md:hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:active:bg-boxdark dark:active:text-gray-200 md:dark:hover:bg-boxdark md:dark:hover:text-gray-200 touch-manipulation"
          aria-label={isExpanded ? "Reduzir modal" : "Expandir modal"}
        >
          {isExpanded ? (
            <FiMinimize2 size={18} className="sm:w-5 sm:h-5 md:w-6 md:h-6" />
          ) : (
            <FiMaximize2 size={18} className="sm:w-5 sm:h-5 md:w-6 md:h-6" />
          )}
        </button>
        <button
          onClick={onClose}
          className="rounded-lg md:rounded-xl p-1.5 sm:p-2 md:p-2.5 text-gray-500 outline-none transition-all active:bg-white active:text-gray-700 active:scale-95 md:hover:bg-white md:hover:text-gray-700 md:hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:active:bg-boxdark dark:active:text-gray-200 md:dark:hover:bg-boxdark md:dark:hover:text-gray-200 touch-manipulation"
          aria-label="Fechar formulário"
        >
          <FiX size={20} className="sm:w-[22px] sm:h-[22px] md:w-6 md:h-6" />
        </button>
      </div>

      <div className="relative flex items-start gap-2 sm:gap-3 md:gap-5 pr-14 sm:pr-16 md:pr-24">
        <div className="relative flex-shrink-0">
          <div className="absolute -inset-0.5 sm:-inset-1 rounded-full bg-gradient-to-r from-purple-600 to-purple-600 opacity-75 blur-sm"></div>
          <div className="relative flex h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 items-center justify-center overflow-hidden rounded-full border-2 sm:border-3 md:border-4 border-white bg-purple-600 shadow-xs dark:border-boxdark">
            <FiActivity className="text-white w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8" />
          </div>
        </div>

        <div className="flex-1 min-w-0 pt-0.5 sm:pt-1">
          <div className="flex items-center gap-2 sm:gap-3">
            <h3 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {title}
            </h3>
          </div>
          <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
            {subtitle}
          </p>
        </div>
      </div>
    </div>
  );
}

interface CollapsibleSectionProps {
  title: string;
  description: string;
  icon: React.ElementType;
  iconBgColor: string;
  iconColor: string;
  isCollapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export function CollapsibleSection({
  title,
  description,
  icon: Icon,
  iconBgColor,
  iconColor,
  isCollapsed,
  onToggle,
  children,
}: CollapsibleSectionProps) {
  return (
    <div className={`rounded-xl sm:rounded-2xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark ${isCollapsed ? 'p-2 sm:p-3' : 'p-3 sm:p-4'}`}>
      <div
        className={`flex cursor-pointer items-center justify-between rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 transition-colors active:bg-gray-50 md:hover:bg-gray-50 dark:active:bg-gray-800/50 md:dark:hover:bg-gray-800/50 touch-manipulation ${!isCollapsed ? 'mb-3 sm:mb-4 md:mb-5' : ''}`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className={`flex-shrink-0 rounded-lg sm:rounded-xl p-1.5 sm:p-2 ${iconBgColor}`}>
            <Icon className={`${iconColor} w-4 h-4 sm:w-5 sm:h-5`} />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 dark:text-white truncate">
              {title}
            </h4>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
              {description}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0 ml-2">
          {isCollapsed ? (
            <FiChevronDown className="text-gray-400 w-4 h-4 sm:w-[18px] sm:h-[18px]" />
          ) : (
            <FiChevronUp className="text-gray-400 w-4 h-4 sm:w-[18px] sm:h-[18px]" />
          )}
        </div>
      </div>
      {!isCollapsed && children}
    </div>
  );
}

interface FormInputProps {
  label: string;
  name: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

export function FormInput({
  label,
  name,
  value,
  onChange,
  placeholder,
}: FormInputProps) {
  return (
    <div>
      <label className="mb-1.5 sm:mb-2 block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      <input
        type="text"
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-lg sm:rounded-xl border border-stroke bg-white px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base font-medium outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation"
      />
    </div>
  );
}

interface AudioRecorderProps {
  onAudioRecorded: (audioBlob: Blob) => void;
  onCancel: () => void;
  autoStart?: boolean;
  loading?: boolean;
}

export function AudioRecorder({
  onAudioRecorded,
  onCancel,
  autoStart = false,
  loading = false,
}: AudioRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasAutoStartedRef = useRef(false);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Detectar o melhor formato suportado
      let mimeType = "audio/webm";
      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        mimeType = "audio/webm;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/webm")) {
        mimeType = "audio/webm";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
      } else if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
        mimeType = "audio/ogg;codecs=opus";
      }

      console.log("Usando formato de áudio:", mimeType);

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        console.log("Áudio gravado:", {
          size: blob.size,
          type: blob.type,
          url,
        });
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      intervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Erro ao iniciar gravação:", error);
      alert("Erro ao acessar o microfone. Verifique as permissões.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  };

  const handlePlayPause = async () => {
    if (!audioRef.current || !audioUrl) {
      console.log("Áudio não disponível:", {
        audioRef: !!audioRef.current,
        audioUrl,
      });
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        console.log("Tentando reproduzir áudio:", {
          readyState: audioRef.current.readyState,
          src: audioRef.current.src,
          currentTime: audioRef.current.currentTime,
        });

        // Se o áudio não estiver carregado, carregar primeiro
        if (
          audioRef.current.readyState === 0 ||
          audioRef.current.readyState === 1
        ) {
          audioRef.current.load();
          // Aguardar o áudio estar pronto
          await new Promise<void>((resolve, reject) => {
            if (!audioRef.current) {
              reject(new Error("Audio element not found"));
              return;
            }
            const timeout = setTimeout(() => {
              reject(new Error("Timeout loading audio"));
            }, 5000);

            const handleCanPlay = () => {
              clearTimeout(timeout);
              if (audioRef.current) {
                audioRef.current.removeEventListener("canplay", handleCanPlay);
                audioRef.current.removeEventListener("error", handleError);
              }
              resolve();
            };
            const handleError = (e: Event) => {
              clearTimeout(timeout);
              if (audioRef.current) {
                audioRef.current.removeEventListener("canplay", handleCanPlay);
                audioRef.current.removeEventListener("error", handleError);
                console.error(
                  "Erro ao carregar áudio:",
                  e,
                  audioRef.current.error
                );
              } else {
                console.error("Erro ao carregar áudio:", e);
              }
              reject(new Error("Failed to load audio"));
            };
            audioRef.current.addEventListener("canplay", handleCanPlay, {
              once: true,
            });
            audioRef.current.addEventListener("error", handleError, {
              once: true,
            });
          });
        }

        // Tentar reproduzir
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
          console.log("Áudio começou a tocar com sucesso");
        }
        // O estado será atualizado pelo evento onPlay
      } catch (error) {
        console.error("Erro ao reproduzir áudio:", error);
        setIsPlaying(false);
        alert(
          `Erro ao reproduzir áudio: ${
            error instanceof Error ? error.message : "Erro desconhecido"
          }`
        );
      }
    }
  };

  const handleCancel = () => {
    // Não permite cancelar durante o loading (enquanto está processando)
    if (loading) {
      return;
    }

    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setIsPlaying(false);
    setIsRecording(false);
    setRecordingTime(0);
    setAudioDuration(0);
    hasAutoStartedRef.current = false;
    onCancel();
  };

  const handleSend = () => {
    if (audioBlob) {
      hasAutoStartedRef.current = false;
      onAudioRecorded(audioBlob);
    }
  };

  useEffect(() => {
    if (autoStart && !hasAutoStartedRef.current) {
      hasAutoStartedRef.current = true;
      startRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  useEffect(() => {
    if (audioUrl && audioRef.current) {
      setIsPlaying(false);
      // Só recarregar se o src mudou
      if (audioRef.current.src !== audioUrl) {
        audioRef.current.load();
      }
    }
  }, [audioUrl]);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [audioUrl]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="rounded-xl border-2 border-purple-300 bg-purple-50 p-4 dark:border-purple-700 dark:bg-purple-900/20">
      {!audioBlob ? (
        <div className="flex items-center gap-4">
          {!isRecording ? (
            <>
              <button
                type="button"
                onClick={startRecording}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500 text-white transition hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                title="Iniciar gravação"
              >
                <FiMic size={20} />
              </button>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Clique para começar a gravar
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Grave a descrição da avaliação física
                </p>
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={stopRecording}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600 text-white transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                title="Parar gravação"
              >
                <FiMicOff size={20} />
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-red-500"></div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Gravando... {formatTime(recordingTime)}
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Clique no microfone para parar
                </p>
              </div>
            </>
          )}
          {isRecording && (
            <button
              type="button"
              onClick={stopRecording}
              className="rounded-lg p-2 text-red-500 transition hover:bg-red-50 hover:text-red-600 dark:text-red-400 dark:hover:bg-red-900/20"
              title="Parar gravação"
            >
              <FiMicOff size={20} />
            </button>
          )}
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-lg p-2 text-gray-500 transition hover:bg-white hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            title="Cancelar"
          >
            <FiX size={20} />
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handlePlayPause}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-600 text-white transition hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              title={isPlaying ? "Pausar" : "Reproduzir"}
            >
              {isPlaying ? <FiPause size={18} /> : <FiPlay size={18} />}
            </button>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Áudio gravado ({formatTime(audioDuration || recordingTime)})
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isPlaying ? "Reproduzindo..." : "Clique para ouvir"}
              </p>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-lg p-2 text-red-500 transition hover:bg-red-50 hover:text-red-600 dark:text-red-400 dark:hover:bg-red-900/20"
              title="Descartar áudio"
            >
              <FiTrash size={18} />
            </button>
          </div>
          <audio
            ref={audioRef}
            src={audioUrl || undefined}
            preload="auto"
            onLoadedMetadata={() => {
              if (audioRef.current) {
                const duration = audioRef.current.duration;
                if (isFinite(duration) && duration > 0) {
                  setAudioDuration(Math.floor(duration));
                }
                console.log("Áudio carregado:", {
                  duration,
                  readyState: audioRef.current.readyState,
                });
              }
            }}
            onCanPlay={() => {
              console.log("Áudio pode ser reproduzido");
            }}
            onEnded={() => {
              console.log("Áudio terminou");
              setIsPlaying(false);
            }}
            onPlay={() => {
              console.log("Áudio começou a tocar");
              setIsPlaying(true);
            }}
            onPause={() => {
              console.log("Áudio pausado");
              setIsPlaying(false);
            }}
            onError={(e) => {
              console.error(
                "Erro ao carregar áudio:",
                e,
                audioRef.current?.error
              );
              setIsPlaying(false);
            }}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={loading}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-2 text-sm font-semibold text-white transition hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Avaliando Informações...
                </>
              ) : (
                "Enviar Áudio"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// AIInputSection foi movido para arquivo separado: ./AIInputSection.tsx

interface FormFooterProps {
  onCancel: () => void;
  onSubmit: () => void;
  loading: boolean;
  submitLabel?: string;
}

export function FormFooter({
  onCancel,
  onSubmit,
  loading,
  submitLabel = "Salvar Avaliação",
}: FormFooterProps) {
  return (
    <div className="border-t border-stroke bg-white px-3 py-3 sm:px-6 sm:py-5 md:px-8 md:py-6 dark:border-strokedark dark:bg-boxdark">
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="w-full sm:w-auto rounded-lg sm:rounded-xl border border-stroke px-4 py-3 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-gray-700 transition-all active:bg-gray-50 active:scale-95 md:hover:bg-gray-50 md:hover:shadow-sm dark:border-strokedark dark:text-gray-300 dark:active:bg-gray-800/50 md:dark:hover:bg-gray-800/50 touch-manipulation"
        >
          Cancelar
        </button>
        <button
          type="submit"
          onClick={onSubmit}
          disabled={loading}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-purple-600 to-purple-600 px-4 py-3 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-sm transition-all active:scale-95 md:hover:scale-105 md:hover:shadow-xl disabled:opacity-50 disabled:active:scale-100 disabled:md:hover:scale-100 touch-manipulation"
        >
          <FiSave size={16} className="sm:w-[18px] sm:h-[18px]" />
          {loading ? "Salvando..." : submitLabel}
        </button>
      </div>
    </div>
  );
}

interface DiffFieldItemProps {
  field: string;
  before: string;
  after: string;
  isEditing: boolean;
  editedValue: string;
  onEdit: () => void;
  onSave: (value: string) => void;
  onCancel: () => void;
  onRemove: () => void;
  onValueChange: (value: string) => void;
}

function DiffFieldItem({
  field,
  before,
  after,
  isEditing,
  editedValue,
  onEdit,
  onSave,
  onCancel,
  onRemove,
  onValueChange,
}: DiffFieldItemProps) {
  const fieldLabel = field.replace(/_/g, " ").replace(/\./g, " → ");

  if (isEditing) {
    return (
      <div className="rounded-lg border-2 border-purple-300 bg-purple-50 p-3 dark:border-purple-700 dark:bg-purple-900/20">
        <div className="mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {fieldLabel}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={editedValue}
            onChange={(e) => onValueChange(e.target.value)}
            className="flex-1 rounded-lg border border-purple-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-purple-600 dark:bg-form-input dark:text-white dark:focus:border-purple-500"
            autoFocus
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                onSave(editedValue);
              } else if (e.key === "Escape") {
                onCancel();
              }
            }}
          />
          <button
            type="button"
            onClick={() => onSave(editedValue)}
            className="rounded-lg bg-green-600 p-2 text-white transition hover:bg-green-700"
            title="Salvar"
          >
            <FiCheck size={16} />
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg bg-gray-400 p-2 text-white transition hover:bg-gray-500"
            title="Cancelar"
          >
            <FiX size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 transition hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/50 dark:hover:bg-gray-800">
      <div className="flex-1">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {fieldLabel}
        </span>
        <div className="mt-1 flex items-center gap-3">
          <span className="text-sm text-gray-500 line-through dark:text-gray-400">
            {before}
          </span>
          <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
            {after}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-lg p-2 text-gray-600 transition hover:bg-white hover:text-purple-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-purple-400"
          title="Editar valor"
        >
          <FiEdit2 size={16} />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="rounded-lg p-2 text-gray-600 transition hover:bg-white hover:text-red-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-red-400"
          title="Remover campo"
        >
          <FiTrash2 size={16} />
        </button>
      </div>
    </div>
  );
}

interface AIModalProps {
  isOpen: boolean;
  onClose: () => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  onAnalyze: () => void;
  onApply: (
    filteredFields: Record<string, { before: string; after: string }>
  ) => void;
  onBack: () => void;
  aiLoading: boolean;
  aiResult: Partial<Avaliation> | null;
  changedFields: Record<string, { before: string; after: string }>;
  hasChanges: boolean;
}

export function AIModal({
  isOpen,
  onClose,
  description,
  onDescriptionChange,
  onAnalyze,
  onApply,
  onBack,
  aiLoading,
  aiResult,
  changedFields,
  hasChanges,
}: AIModalProps) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [removedFields, setRemovedFields] = useState<Set<string>>(new Set());

  if (!isOpen) return null;

  const handleEdit = (field: string, currentValue: string) => {
    setEditingField(field);
    setEditedValues((prev) => ({
      ...prev,
      [field]: currentValue,
    }));
  };

  const handleSaveEdit = (field: string, value: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    setEditingField(null);
  };

  const handleCancelEdit = () => {
    setEditingField(null);
  };

  const handleRemove = (field: string) => {
    setRemovedFields((prev) => new Set(prev).add(field));
    setEditingField(null);
    setEditedValues((prev) => {
      const newValues = { ...prev };
      delete newValues[field];
      return newValues;
    });
  };

  const handleApply = () => {
    const filteredFields: Record<string, { before: string; after: string }> =
      {};

    Object.entries(changedFields).forEach(([field, { before, after }]) => {
      if (!removedFields.has(field)) {
        filteredFields[field] = {
          before,
          after: editedValues[field] || after,
        };
      }
    });

    onApply(filteredFields);
  };

  const visibleFields = Object.entries(changedFields).filter(
    ([field]) => !removedFields.has(field)
  );

  return (
    <div className="fixed inset-0 z-[999999999] flex items-center justify-center bg-black/60 p-0 sm:p-4 sm:backdrop-blur-sm">
      <div className="relative flex h-full w-full sm:h-[85vh] sm:max-w-4xl flex-col overflow-hidden sm:rounded-2xl border-0 sm:border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark">
        {/* Header */}
        <div className="relative border-b border-stroke bg-gradient-to-r from-purple-50 via-purple-50 to-purple-50 px-3 py-3 sm:px-6 sm:py-5 md:px-8 md:py-6 dark:border-strokedark dark:from-purple-950/30 dark:via-purple-950/30 dark:to-purple-950/30">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

          <button
            onClick={onClose}
            className="absolute right-2 top-2 sm:right-4 sm:top-4 md:right-6 md:top-6 z-10 rounded-lg md:rounded-xl p-1.5 sm:p-2 md:p-2.5 text-gray-500 outline-none transition-all active:bg-white active:text-gray-700 active:scale-95 md:hover:bg-white md:hover:text-gray-700 md:hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:active:bg-boxdark dark:active:text-gray-200 md:dark:hover:bg-boxdark md:dark:hover:text-gray-200 touch-manipulation"
            aria-label="Fechar modal"
          >
            <FiX size={20} className="sm:w-[22px] sm:h-[22px] md:w-6 md:h-6" />
          </button>

          <div className="relative flex items-start gap-2 sm:gap-3 md:gap-5 pr-12 sm:pr-14 md:pr-20">
            <div className="relative flex-shrink-0">
              <div className="absolute -inset-0.5 sm:-inset-1 rounded-full bg-gradient-to-r from-purple-600 to-purple-600 opacity-75 blur-sm"></div>
              <div className="relative flex h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 items-center justify-center overflow-hidden rounded-full border-2 sm:border-3 md:border-4 border-white bg-gradient-to-r from-purple-600 to-purple-700 shadow-xs dark:border-boxdark">
                <FiZap className="text-white w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
              </div>
            </div>

            <div className="flex-1 min-w-0 pt-0.5 sm:pt-1">
              <h3 className="text-base sm:text-lg md:text-2xl font-bold text-gray-900 dark:text-white">
                Preencher com IA
              </h3>
              <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2 sm:line-clamp-none">
                Descreva a avaliação física e a IA preencherá os campos
                automaticamente
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto bg-gray-50/50 p-3 sm:p-6 md:p-8 dark:bg-gray-900/20 overscroll-contain">
            {!aiResult ? (
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="mb-2 sm:mb-3 block text-xs sm:text-sm font-bold text-gray-700 dark:text-gray-300">
                    Descrição da Avaliação Física
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                    placeholder="Aluno do sexo masculino, 30 anos, 75.5 kg, 175 cm de altura. Braço direito 35 cm, braço esquerdo 34 cm. Tórax 95 cm, cintura 82 cm, quadril 92 cm. Dobra tricipal 12 mm, subescapular 15 mm. Bioimpedância: 18% gordura, 42% músculo."
                    className="w-full min-h-[150px] sm:min-h-[200px] rounded-lg sm:rounded-xl border border-stroke bg-white px-3 py-2.5 sm:px-5 sm:py-4 text-sm sm:text-base font-medium text-gray-900 outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation"
                    disabled={aiLoading}
                  />
                  <p className="mt-1.5 sm:mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Descreva as medidas, dobras cutâneas e dados de
                    bioimpedância disponíveis. A IA extrairá apenas os dados que
                    puder identificar claramente.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {/* Comparação Antes/Depois */}
                {hasChanges && (
                  <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-3 sm:p-5 md:p-6 shadow-sm dark:border-strokedark dark:bg-boxdark">
                    <h4 className="mb-3 sm:mb-4 flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base md:text-lg font-bold text-gray-900 dark:text-white">
                      <FiActivity className="text-purple-600 w-4 h-4 sm:w-5 sm:h-5" />
                      Campos que serão alterados
                    </h4>
                    {visibleFields.length === 0 ? (
                      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                        <p className="text-sm text-yellow-800 dark:text-yellow-300">
                          Todos os campos foram removidos. Nenhuma alteração
                          será aplicada.
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-[400px] space-y-3 overflow-y-auto">
                        {visibleFields.map(([field, { before, after }]) => (
                          <DiffFieldItem
                            key={field}
                            field={field}
                            before={before}
                            after={editedValues[field] || after}
                            isEditing={editingField === field}
                            editedValue={editedValues[field] || after}
                            onEdit={() =>
                              handleEdit(field, editedValues[field] || after)
                            }
                            onSave={(value) => handleSaveEdit(field, value)}
                            onCancel={handleCancelEdit}
                            onRemove={() => handleRemove(field)}
                            onValueChange={(value) =>
                              setEditedValues((prev) => ({
                                ...prev,
                                [field]: value,
                              }))
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Campos Novos (sem valores anteriores) */}
                {!hasChanges && (
                  <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 dark:border-blue-800 dark:bg-blue-900/20">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      A IA identificou dados que serão adicionados aos campos
                      vazios do formulário.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Fixo */}
          <div className="border-t border-stroke bg-white px-3 py-3 sm:px-6 sm:py-5 md:px-8 md:py-6 dark:border-strokedark dark:bg-boxdark">
            {!aiResult ? (
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto rounded-lg sm:rounded-xl border border-stroke px-4 py-3 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-gray-700 transition-all active:bg-gray-50 active:scale-95 md:hover:bg-gray-50 md:hover:shadow-sm dark:border-strokedark dark:text-gray-300 dark:active:bg-gray-800/50 md:dark:hover:bg-gray-800/50 touch-manipulation"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={onAnalyze}
                  disabled={aiLoading || !description.trim()}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-3 sm:px-8 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-sm transition-all active:scale-95 md:hover:scale-105 md:hover:shadow-xl disabled:opacity-50 disabled:active:scale-100 disabled:md:hover:scale-100 touch-manipulation"
                >
                  {aiLoading ? (
                    <>
                      <div className="h-4 w-4 sm:h-5 sm:w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span className="hidden sm:inline">Avaliando Informações...</span>
                      <span className="sm:hidden">Avaliando...</span>
                    </>
                  ) : (
                    <>
                      <FiZap size={16} className="sm:w-[18px] sm:h-[18px]" />
                      <span className="hidden sm:inline">Analisar Descrição</span>
                      <span className="sm:hidden">Analisar</span>
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={onBack}
                  className="w-full sm:w-auto rounded-lg sm:rounded-xl border border-stroke px-4 py-3 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-gray-700 transition-all active:bg-gray-50 active:scale-95 md:hover:bg-gray-50 md:hover:shadow-sm dark:border-strokedark dark:text-gray-300 dark:active:bg-gray-800/50 md:dark:hover:bg-gray-800/50 touch-manipulation"
                >
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={visibleFields.length === 0}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-green-600 to-green-700 px-4 py-3 sm:px-8 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-sm transition-all active:scale-95 md:hover:scale-105 md:hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 disabled:md:hover:scale-100 touch-manipulation"
                >
                  <FiCheck size={16} className="sm:w-[18px] sm:h-[18px]" />
                  Aplicar Campos{" "}
                  {visibleFields.length > 0 && `(${visibleFields.length})`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface PhotosSectionProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
}

export function PhotosSection({ images, onImagesChange }: PhotosSectionProps) {
  return (
    <div className="p-1">
      <ImageInput
        multiple
        label="Fotos da Avaliação"
        value={images || []}
        onChange={(images) => {
          onImagesChange(images as string[]);
        }}
        classNameImage="!h-[250px] !w-full"
      />
    </div>
  );
}
